<script setup lang="ts">
import type { GradeCode } from '~/models/Grade'
import type { ContentSectionStandalone } from '~/models/Content/ContentSectionStandalone'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import useAuthStore from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import useContentApi from '~/api/contentApi'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import LibrarySearchRelevantCard from '~/components/products/library/LibrarySearchRelevantCard.vue'

const props = defineProps<{ pathString: string, grades: GradeCode[] }>()

const { findContents } = useContentApi()
const { isAuthenticated } = storeToRefs(useAuthStore())

const { data: sections, isLoading } = useQuery({
  queryKey: ['product_relevant_list', props.pathString],
  queryFn: () => findContents<ContentSectionStandalone>({
    subtreeCriterion: [props.pathString],
    sortField: 'priority',
    sortOrder: 'asc',
    contentTypeCriterion: [ContentType.SectionStandalone],
  }),
  enabled: computed(() => isAuthenticated.value && !!props.pathString),
  staleTime: Infinity
})

const filteredSections = computed(() => (sections.value ?? [])
  .filter(section => props.grades.length === 0 || section.grades.some(grade => props.grades.includes(grade)))
  .slice(0, 2))
</script>

<template>
  <div v-if="isLoading || filteredSections.length > 0" class="grid-in-relevant mt-2 mb-6">
    <AuSkeletonWrapper
      v-if="isLoading"
      class="grid grid-cols-1 gap-4 lg:grid-cols-2"
    >
      <AuSkeleton
        v-for="i in 2"
        :key="i"
        height="190px"
        border-radius="0"
      />
    </AuSkeletonWrapper>
    <ul
      v-else-if="filteredSections.length > 0"
      class="grid grid-cols-1 gap-4"
      :class="filteredSections.length > 1 ? 'lg:grid-cols-2' : 'lg:grid-cols-1'"
    >
      <li
        v-for="section in filteredSections"
        :key="section.locationId"
      >
        <LibrarySearchRelevantCard :section />
      </li>
    </ul>
  </div>
</template>
